import BreadCrumbs from "@/components/Gallery/BreadCrumbs";
import Link from "@/components/Link";
import Button, { ButtonType } from "@/components/reusable/Button";
import CategoryTabs from "@/components/shared/CategoryTabs";
import GenericPagination from "@/components/shared/GenericPagination";
import { getReadableImageUrl } from "@/lib/directus";
import { getGalleryCategoryUrl, getGalleryPatientUrl } from "@/lib/gallery";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface ProcedureSectionProps {
  data: {
    procedure: {
      title: string;
      slug: string;
    };
    patient: {
      id: string;
      title: string;
      history: string;
      photos: {
        site_content_collections_id: {
          collection: {
            item: {
              image_file: {
                image: {
                  id: string;
                  filename_download: string;
                };
              };
              alt?: string;
            };
          }[];
        };
      }[];
    };
    patients: {
      id: string;
      title: string;
      photos: {
        site_content_collections_id: {
          collection: {
            item: {
              image_file: {
                image: {
                  id: string;
                  filename_download: string;
                };
              };
              alt?: string;
            };
          }[];
        };
      }[];
    }[];
    categories: {
      id: string;
      slug: string;
      title: string;
    }[];
    procedureSlug: string;
    currentPage: number;
    totalPages: number;
  };
}
export const fields = ["*"] as const;

export default function ProcedureSection({ data }: ProcedureSectionProps) {
  const galleryBaseUrl = `/${process.env.NEXT_PUBLIC_GALLERY_SLUG}`;
  const galleryCategoryUrl = `${galleryBaseUrl}/${data.procedureSlug}`;

  // Extract categories from data, ensuring they have the required id field
  const categories = (data?.categories || []).map(
    (category: any, index: number) => ({
      id: category.id || category.slug || index.toString(),
      slug: category.slug,
      title: category.title,
    })
  );

  return (
    <>
      <BreadCrumbs
        className="bg-accent-color-three-dark text-[#fff] py-6 px-6 md:py-6 md:px-12 lg:py-8 lg:px-32 m-0 text-span-medium"
        segments={[
          {
            title: data.procedure.title,
            url: getGalleryCategoryUrl(data.procedure.slug),
          },
        ]}
      />
      <section
        id="ProcedureSection"
        className="bg-accent-color-three py-12 px-6 md:px-12 md:py-16 lg:py-36 lg:px-36"
      >
        <div className="mx-auto">
          <div className="flex flex-col gap-12 lg:gap-20">
            {/* Category Navigation */}
            <div className="BlogNav max-md:justify-center max-md:flex">
              <div className="flex justify-between items-center">
                <CategoryTabs
                  categories={categories}
                  baseUrl={galleryBaseUrl}
                  viewAllLabel="VIEW ALL"
                />
              </div>
            </div>

            {/* Gallery Grid */}
            <div>
              <div className="space-y-16">
                {data?.patients?.map(
                  (patient, index: number) =>
                    patient.photos &&
                    patient.photos.length > 0 && (
                      <div
                        key={index}
                        className="flex flex-col lg:flex-row gap-6 max-w-[1632px] mx-auto"
                      >
                        {/* Images Grid - Left Side */}

                        <div className="relative w-full lg:max-w-[calc(100%-20rem-1.5rem)]">
                          <Swiper
                            modules={[Navigation, Pagination]}
                            spaceBetween={24}
                            slidesPerView={1}
                            breakpoints={{
                              1366: { slidesPerView: 2 },
                              1600: { slidesPerView: 3 },
                            }}
                            watchOverflow={true}
                            navigation={{
                              nextEl: `.swiper-button-next-${patient.id}`,
                              prevEl: `.swiper-button-prev-${patient.id}`,
                              enabled: true,
                            }}
                          >
                            {patient.photos?.map((photoSet, setIndex) => (
                              <SwiperSlide
                                key={`${setIndex}-${patient.id}`}
                              >
                                <Link
                                  href={getGalleryPatientUrl(
                                    data.procedureSlug,
                                    patient.id
                                  )}
                                  className="block"
                                >
                                  <div className="flex gap-3 cursor-pointer hover:opacity-90 transition-opacity duration-300">
                                    {photoSet.site_content_collections_id?.collection?.map(
                                      (collectionItem, idx) => {
                                        const imageData =
                                          collectionItem.item?.image_file?.image;
                                        if (!imageData) return null; // Skip if no image data
                                        return (
                                          <div
                                            key={idx}
                                            className="relative w-[189px] h-auto aspect-[0.81]"
                                          >
                                            <Image
                                              key={idx}
                                              src={getReadableImageUrl(
                                                imageData.id,
                                                imageData.filename_download
                                              )}
                                              alt={
                                                collectionItem.item.alt ||
                                                "Before & After Procedure"
                                              }
                                              width={189}
                                              height={233}
                                              className="object-contain w-full h-full"
                                              quality={95}
                                              priority={index < 2 && setIndex < 2}
                                            />
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </Link>
                              </SwiperSlide>
                            ))}
                          </Swiper>

                          {/* Navigation buttons for this specific slider */}
                          {patient.photos?.length > 1 && (
                            <>
                              <button
                                className={`swiper-button-prev-${patient.id} absolute left-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-accent-color-two-25 hover:bg-accent-color-two-dark focus:bg-accent-color-two-dark transition-colors duration-300 z-10`}
                                aria-label="Previous images"
                              >
                                <span className="text-4xl text-secondary font-bold">
                                  &#60;
                                </span>
                              </button>

                              <button
                                className={`swiper-button-next-${patient.id} absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-accent-color-two-25 hover:bg-accent-color-two-dark focus:bg-accent-color-two-dark transition-colors duration-300 z-10`}
                                aria-label="Next images"
                              >
                                <span className="text-4xl text-secondary font-bold">
                                  &#62;
                                </span>
                              </button>
                            </>
                          )}

                          {/* Fallback for when there are no images */}
                          {(!patient.photos || patient.photos.length === 0) && (
                            <div className="flex gap-[5px]">
                              <div className="aspect-[4/5] bg-gray-800 flex items-center justify-center flex-1">
                                <span className="text-gray-500">
                                  No Image Available
                                </span>
                              </div>
                              <div className="aspect-[4/5] bg-gray-800 flex items-center justify-center flex-1">
                                <span className="text-gray-500">
                                  No Image Available
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Patient Info - Right Side */}
                        <div className="lg:w-80 flex flex-col justify-center">
                          <div className="text-center lg:text-left">
                            <div className="text-h3-mobile md:text-h3-tablet lg:text-h3-desktop uppercase text-secondary">
                              {patient.title || `Case ID: ${patient.id}`}
                            </div>
                            <p className="text-sm text-gray-300 mt-6">
                              Individual results may vary.
                            </p>
                            <Button
                              type={ButtonType.gallery}
                              link={getGalleryPatientUrl(
                                data.procedureSlug,
                                patient.id
                              )}
                              className="mt-6"
                              rightIcon={<ArrowRight className="w-6 h-6" />}
                            >
                              VIEW FULL CASE
                            </Button>
                          </div>
                        </div>
                      </div>
                    )
                )}
              </div>
            </div>
            {/* Pagination */}
            <GenericPagination
              currentPage={data.currentPage || 1}
              totalPages={data.totalPages}
              baseUrl={galleryCategoryUrl}
              pageSlug={process.env.NEXT_PUBLIC_GALLERY_PAGE_SLUG || "page"}
            />
          </div>
        </div>
      </section>
    </>
  );
}
